import '../App.css';
import Navbar from '../components/Navbar';
import IndustriesSlide from '../components/IndustriesSlide';
import ProcessBlocks from '../components/ProcessBlocks';
// import NextSteps from '../components/NextSteps';
import ScrollStack, { ScrollStackItem } from '../components/ScrollStack';
import HeroStagger, { HeroStaggerItem } from '../components/HeroStagger';
import { Link } from 'react-router-dom';
import {motion} from 'framer-motion';
import PageHelmet from '../components/PageHelmet';


const JOURNEY_STEPS = [
  {
    title: 'Discover',
    subtitle: 'Identify risk exposure and data gaps',
    icon: 'fa-solid fa-magnifying-glass',
    bullets: [
      'Portfolio review and initial conversations',
      'Pinpoint missing or outdated COPE, NatCat, and engineering data',
      'Align on goals: underwriting, cost savings, and visibility',
    ],
    img: '/img/home/discover.jpg'
  },
  {
    title: 'Capture',
    subtitle: 'Collect accurate, real-time property data',
    icon: 'fa-solid fa-camera',
    bullets: [
      'Onsite HPR surveys or remote audits',
      'Mobile-first data collection (photos, geolocation, structured inputs)',
      'No paper, no delays',
    ],
    img: '/img/home/capture.jpg'
  },
  {
    title: 'Structure',
    subtitle: 'Turn raw data into standardized digital assets',
    icon: 'fa-solid fa-diagram-project',
    bullets: [
      'Build Digital Property Profiles',
      'Organize data through SnapCOPE, SnapCAT, and other modules',
      'Clean, validated, model-ready datasets',
    ],
    img: '/img/home/structure.jpg'
  },
  {
    title: 'Visualize',
    subtitle: 'Transform data into portfolio-level insight',
    icon: 'fa-solid fa-chart-line',
    bullets: [
      'Map risk across locations',
      'Layer in hazard data (flood, wildfire, earthquake)',
      'Identify trends, concentrations, and blind spots',
    ],
    img: '/img/home/visualize.jpg'
  },
  {
    title: 'Act',
    subtitle: 'Drive risk improvement and engineering action',
    icon: 'fa-solid fa-bolt',
    bullets: [
      'Deliver reports instantly',
      'Track recommendations through SnapREC',
      'Prioritize mitigation efforts with confidence',
    ],
    img: '/img/home/act.jpg'
  },
  {
    title: 'Optimize',
    subtitle: 'Continuously improve and inform decisions',
    icon: 'fa-solid fa-gears',
    bullets: [
      'Maintain living, updateable data',
      'Support underwriting and renewal strategy',
      'Reduce costs and strengthen risk posture',
    ],
    img: '/img/home/optimize.jpg'
  },
];

function Home() {
  return (
    <div className="app relative">
      <PageHelmet
        title="SnapRISK"
        description="SnapRISK is a global technology company specializing in property risk engineering. We help organizations navigate complex property exposures through innovative technology, deep engineering expertise, and hands-on field experience."
        path="/"
      />
      <section
        id="home"
        data-no-reveal
        className="relative flex min-h-[min(100vh,52rem)] w-full flex-col overflow-hidden"
      >
        <video
          className="pointer-events-none absolute inset-0 z-0 h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden
        >
          <source src="/img/home/homepage_video_longer.mp4" type="video/mp4" />
        </video>

        {/* Scrim: light panel for copy on the left, subtle darken on the right */}
        <div
          className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-r from-white/95 via-white/75 to-white/25 md:from-white/92 md:via-white/55 md:to-transparent"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-t from-primary/15 via-transparent to-white/40"
          aria-hidden
        />

        <div className="hero relative sticky top-0 z-30">
          <Navbar />
        </div>

        <HeroStagger className="relative z-10 flex flex-1 flex-col items-start justify-center px-6 pb-16 pt-6 md:px-16 md:pb-20 md:pt-8 lg:px-20">
          <HeroStaggerItem>
            <p className="m-0 text-sm font-semibold uppercase tracking-[0.22em] text-primary/80">Welcome</p>
          </HeroStaggerItem>
          <HeroStaggerItem>
            <h1 className="mt-3 max-w-3xl text-4xl font-bold leading-tight tracking-tight text-primary md:text-6xl">
              Together in Risk
            </h1>
          </HeroStaggerItem>
          <HeroStaggerItem>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-gray-800 md:mt-8 md:text-xl">
              SnapRISK is a global technology company specializing in property risk engineering. We help
              organizations navigate complex property exposures through innovative technology, deep engineering
              expertise, and hands-on field experience.
            </p>
          </HeroStaggerItem>
          <HeroStaggerItem>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/solutions"
                className="rounded-md bg-primary px-5 py-2.5 font-medium text-white shadow-md transition-opacity hover:opacity-90"
              >
                View Solutions
              </Link>
              <Link
                to="/contact?type=demo"
                className="rounded-md border border-primary/40 bg-white/90 px-5 py-2.5 font-medium text-primary shadow-sm backdrop-blur-sm transition-colors hover:bg-white"
              >
                Request a Demo
              </Link>
            </div>
          </HeroStaggerItem>
        </HeroStagger>
      </section>

      <motion.div   
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}>
      <section className="bg-white py-12 md:py-16">
        <div className="sticky top-0 z-20 mx-auto max-w-6xl border-b border-primary/10 bg-white/95 px-6 py-4 text-center backdrop-blur md:px-10 lg:px-16">
          {/* <p className="m-0 text-xl font-semibold uppercase tracking-[0.25em] text-primary">
            The Journey with <span className= "snaprisk-font">S</span><span className= "snaprisk-font lowercase">nap</span><span className= "snaprisk-font uppercase">RISK</span>
          </p> */}
          <p className="m-0 text-xl font-bold font-montserrat tracking-[0.25em] text-primary">
            The Journey with SnapRISK
          </p>
          <p className="mx-auto mt-3 max-w-lg text-base leading-relaxed text-gray-600 md:text-lg">
            Six stages from first conversation to lasting partnership.
          </p>
        </div>
        <ScrollStack
          useWindowScroll
          className="mt-4"
          itemDistance={52}
          stackPosition="26%"
          scaleEndPosition="14%"
          baseScale={0.82}
          itemScale={0.018}
          itemStackDistance={14}
        >
          {JOURNEY_STEPS.map((step, index) => (
            <ScrollStackItem
              key={step.title}
              itemClassName="mx-auto max-w-4xl border border-primary/15 bg-white text-left shadow-[0_12px_30px_rgba(15,23,42,0.12)]"
            >
              <div className="relative pr-20 md:pr-48 lg:pr-60">
                <div className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2">
                  <img
                    src={step.img}
                    alt={`${step.title} step`}
                    className="h-14 w-14 rounded-lg object-cover shadow-sm md:h-48 md:w-48 lg:h-60 lg:w-60"
                    loading="lazy"
                  />
                </div>
                <p className="m-0 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                  Stop {index + 1} of {JOURNEY_STEPS.length}
                </p>
                <p className="mt-2 text-sm font-medium italic text-gray-500 md:text-base">{step.subtitle}</p>
                <h3 className="mt-3 text-2xl font-semibold tracking-tight text-gray-900">{step.title}</h3>
                <ul className="mt-3 space-y-2 pl-5 text-base leading-relaxed text-gray-600">
                  {step.bullets.map((item) => (
                    <li key={item} className="list-none">
                      <span className="mr-2 text-primary">➢</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollStackItem>
          ))}
        </ScrollStack>
      </section>

      <section className='text-center bg-secondary py-16'>
        <p className="m-0 text-xl font-bold tracking-[0.25em] text-primary font-montserrat">Industries</p>
        <p className="mx-auto mt-3 max-w-lg text-base leading-relaxed text-gray-600 md:text-lg">We bring deep expertise across a broad range of industries, enabling us to deliver tailored solutions regardless of sector.</p>
        <div className='mx-16 mt-10'>
          <IndustriesSlide />
        </div>
      </section>

      <section className="px-6 py-16 md:px-10 lg:px-16 text-center">
      <p className="m-0 text-xl font-bold tracking-[0.25em] text-primary font-montserrat">Ready to dig a little deeper?</p>
      <p className="mx-auto mt-3 max-w-lg text-base leading-relaxed text-gray-600 md:text-lg">Take a deeper look into our company.</p>
        <div className="mx-auto max-w-6xl mt-6">
          <ProcessBlocks />
        </div>
      </section>
      </motion.div>

      {/* <section
        className="relative overflow-hidden px-6 pb-20 pt-10 text-center md:px-10 lg:px-16"
        style={{
          backgroundImage: "url('/img/home/nextsteps.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/35"/>
        <div className="relative z-10">
          <p className="m-0 text-xl font-semibold uppercase tracking-[0.25em] text-white mx-96 border-b-2 pb-8">NEXT STEPS</p>
          <NextSteps />
        </div>
      </section> */}
    </div>
  );
}

export default Home;
